import React from 'react';
import { Icon } from '@iconify/react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { usePendingEpisodes, useApproveEpisode, useProcessingContent, useRetryPushEpisode } from '../../hooks/useMovie';
import { Button } from '../../components/ui/button';
import { Link } from '../../components/common/NextAdapters';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../../api/errors';

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

export default function AdminEpisodesPage() {
  usePageTitle('Manage Episodes · Kast');
  const { data: episodes = [], isLoading, error, refetch } = usePendingEpisodes();
  const approveMutation = useApproveEpisode();
  const { data: processing } = useProcessingContent();
  const retryMutation = useRetryPushEpisode();
  const stuckEpisodes = processing?.episodes || [];

  const handle = async (episodeId, status) => {
    try {
      const { data } = await approveMutation.mutateAsync({ episodeId, status });
      if (data?.liveNow === false) {
        toast.success("Approved — video is uploading to Bunny.net. It'll go live automatically once that finishes.");
      } else {
        toast.success(`Episode ${status.toLowerCase()}.`);
      }
      refetch();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Action failed'));
    }
  };

  const handleRetry = async (id) => {
    try {
      await retryMutation.mutateAsync(id);
      toast.success('Retry queued — running in the background.');
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Retry failed'));
    }
  };

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
            <Icon icon="ph:play-circle-bold" className="size-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Pending Episodes</h1>
            <p className="text-xs text-muted-foreground">Review and approve episodes awaiting publish</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {episodes.length > 0 && (
            <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary ring-1 ring-primary/25">
              {episodes.length} pending
            </span>
          )}
          <Button size="sm" asChild className="gap-1.5">
            <Link href="/upload/episode">
              <Icon icon="ph:upload-simple-bold" />
              Upload Episode
            </Link>
          </Button>
        </div>
      </div>

      {isLoading ? (
        <EmptyState icon="ph:spinner-gap-bold">Loading…</EmptyState>
      ) : error ? (
        <EmptyState icon="ph:warning-circle-bold">{getApiErrorMessage(error, 'Failed to load pending episodes.')}</EmptyState>
      ) : episodes.length === 0 ? (
        <EmptyState icon="ph:check-circle-bold">No pending episodes</EmptyState>
      ) : (
        <div className="flex flex-col gap-4">
          {episodes.map((ep) => (
            <div
              key={ep.Id}
              className="flex flex-col gap-4 rounded-xl bg-white/[0.03] p-4 ring-1 ring-foreground/[0.06] transition-colors hover:bg-white/[0.05] sm:flex-row"
            >
              <div className="flex h-[170px] w-full shrink-0 items-center justify-center rounded-lg bg-primary/10 text-2xl font-extrabold text-primary sm:h-[135px] sm:w-[100px]">
                {ep.EpisodeNumber}
              </div>
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="flex-1">
                  <h3 className="text-base font-semibold text-foreground">{ep.Title}</h3>
                  <p className="mt-1 flex flex-wrap gap-3 text-xs text-muted-foreground">
                    {ep.MovieTitle && <span>Movie: {ep.MovieTitle}</span>}
                    {ep.SeasonNumber != null && <span>Season {ep.SeasonNumber}</span>}
                    {ep.UploadedBy && <span>by {ep.UploadedBy}</span>}
                    <span>{ep.DateUploaded?.split('T')[0]}</span>
                  </p>
                  {ep.Description && (
                    <p className="mt-2 text-xs leading-relaxed text-muted-foreground/80">
                      {ep.Description?.slice(0, 160)}…
                    </p>
                  )}
                  {ep.VideoUrl && (
                    <div className="mt-2.5 flex flex-wrap gap-4">
                      <a
                        href={ep.VideoUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                      >
                        <Icon icon="ph:play-bold" className="size-3.5" />
                        Watch Episode
                      </a>
                      <a
                        href={ep.VideoUrl}
                        download
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                      >
                        <Icon icon="ph:download-bold" className="size-3.5" />
                        Download
                      </a>
                    </div>
                  )}
                </div>
                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    size="sm"
                    onClick={() => handle(ep.Id, 'Approved')}
                    disabled={approveMutation.isPending}
                    className="gap-1.5 bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30 hover:bg-emerald-500/25"
                  >
                    <Icon icon="ph:check-bold" />
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handle(ep.Id, 'Rejected')}
                    disabled={approveMutation.isPending}
                    className="gap-1.5 bg-destructive/10 text-destructive ring-1 ring-destructive/25 hover:bg-destructive/20"
                  >
                    <Icon icon="ph:x-bold" />
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {stuckEpisodes.length > 0 && (
        <>
          <h2 className="mb-2.5 mt-8 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">
            <Icon icon="ph:clock-bold" className="size-3.5" />
            Waiting on Bunny.net ({stuckEpisodes.length})
          </h2>
          <div className="flex flex-col gap-3">
            {stuckEpisodes.map((ep) => (
              <div key={ep.Id} className="flex items-start gap-4 rounded-xl bg-white/[0.03] p-4 ring-1 ring-foreground/[0.06]">
                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-sm font-semibold text-foreground">{ep.Title}</h3>
                  <p className="mt-1 flex flex-wrap items-center gap-3">
                    {ep.MovieTitle && <span className="text-xs text-muted-foreground">Movie: {ep.MovieTitle}</span>}
                    {ep.BunnyPushError ? (
                      <span className="inline-flex items-center rounded-full bg-destructive/10 px-2.5 py-0.5 text-[11px] font-semibold text-destructive ring-1 ring-destructive/25">
                        Push failed ({ep.BunnyPushAttempts} attempt{ep.BunnyPushAttempts === 1 ? '' : 's'})
                      </span>
                    ) : (
                      <span className="inline-flex items-center rounded-full bg-[#f0a818]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[#f0a818] ring-1 ring-[#f0a818]/30">
                        Uploading…
                      </span>
                    )}
                  </p>
                  {ep.BunnyPushError && <p className="mt-1.5 break-words text-xs text-destructive">{ep.BunnyPushError}</p>}
                </div>
                <Button
                  size="sm"
                  onClick={() => handleRetry(ep.Id)}
                  disabled={retryMutation.isPending}
                  className="shrink-0 gap-1.5 bg-[#f0a818]/10 text-[#f0a818] ring-1 ring-[#f0a818]/30 hover:bg-[#f0a818]/20"
                >
                  <Icon icon="ph:arrow-clockwise-bold" />
                  Retry
                </Button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
